import {
    HTTP_INTERCEPTORS,
    HttpContextToken,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi
} from '@angular/common/http';
import {ApplicationConfig, inject, Injectable, InjectionToken, Injector, provideAppInitializer} from '@angular/core';
import {MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_TABS_CONFIG} from '@angular/material/tabs';
import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    PreloadAllModules,
    provideRouter,
    RouteReuseStrategy,
    withPreloading,
    withRouterConfig
} from '@angular/router';

import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {BasicAuthInterceptor} from "@/app/interceptors/basic-auth.interceptor";
import {routes} from "@/app/routes";
import {MAT_SNACK_BAR_DEFAULT_OPTIONS} from "@angular/material/snack-bar";
import {AuthService} from "@services/auth.service";
import {
    AbstractSecurityStorage,
    LogLevel,
    OidcSecurityService,
    provideAuth
} from "angular-auth-oidc-client";
import {checkAuthOnStart, TabAwareSecurityStorage} from "@services/tab-aware-auth";
import {environment} from "@/environments/environment";
import {loadingInterceptor} from "@/app/interceptors/loading.interceptor";

export const PERSON_IAS_SPORTSMAN = new InjectionToken<number>('person-ias-sportsman');

@Injectable()
export class CustomReuseStrategy implements RouteReuseStrategy {
    handlers: { [key: string]: DetachedRouteHandle } = {};
    private disableReuse = false;
    private authInitialized = false;

    constructor(private injector: Injector) {}

    private ensureAuthInitialized(): void {
        if (this.authInitialized) return;
        this.authInitialized = true;
        try {
            const auth = this.injector.get(AuthService);
            this.disableReuse = !auth.isAuthenticated();
            // Признак «вошёл» берём из потока библиотеки, а не из копии пользователя в хранилище.
            auth.isAuthenticated$.subscribe((isAuthenticated) => {
                this.disableReuse = !isAuthenticated;
                this.clearHandles();
            });
        } catch {
            this.disableReuse = true;
        }
    }

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        //Сохраняем состояние только если reuse === true и пользователь залогинен
        this.ensureAuthInitialized();
        return !this.disableReuse && route.data && !!route.data.reuse;
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        this.ensureAuthInitialized();
        if (!this.disableReuse &&route.data && !!route.data.reuse) {
            const key = this.getRouteKey(route);
            this.handlers[key] = handle;
        }
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        this.ensureAuthInitialized();
        const key = this.getRouteKey(route);
        // Восстанавливаем компонент если reuse === true, пользователь залогинен и есть сохраненный хендл
        return !this.disableReuse && !!route.data && !!route.data.reuse && !!this.handlers[key];
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        this.ensureAuthInitialized();
        if (this.disableReuse || !route.data || !route.data.reuse) return null;
        const key = this.getRouteKey(route);
        return this.handlers[key] || null;
    }

    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig &&
            JSON.stringify(future.params) === JSON.stringify(curr.params);
    }

    private getRouteKey(route: ActivatedRouteSnapshot): string {
        return route.pathFromRoot.map(r => r.url.map(segment => segment.path).join('/')).join('_');
    }

    private clearHandles() {
        this.handlers = {};
    }
}

export const SKIP_LOADING_CONTEXT = new HttpContextToken<boolean>(() => false);

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, withPreloading(PreloadAllModules),
            withRouterConfig({paramsInheritanceStrategy: 'always'})),
        provideHttpClient(
            withFetch(),
            withInterceptors([loadingInterceptor]),
            withInterceptorsFromDi()
        ),
        // Вход проверяется до старта приложения (provideAppInitializer ниже),
        // поэтому к моменту работы гарда состояние сессии уже известно.
        provideAuth({
            config: {
                authority: environment.oidcAuthority,
                redirectUrl: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
                clientId: environment.oidcClientId,
                responseType: 'code',
                scope: environment.oidcScope,
                silentRenew: true,
                useRefreshToken: true,
                renewTimeBeforeTokenExpiresInSeconds: 30,
                logLevel: LogLevel.Warn
            }
        }),
        // Токены в localStorage: они общие для вкладок, поэтому logout в одной вкладке
        // и frontchannel-разлогин от Keycloak гасят сессию сразу во всех. Вход и обновление
        // токена, идущие во вкладке, — в её sessionStorage, иначе вкладки сбивают друг другу state (#29300).
        {provide: AbstractSecurityStorage, useClass: TabAwareSecurityStorage},
        // checkAuth() до старта; при истёкшем access-токене — обновление по refresh-токену, без перехода на Keycloak.
        provideAppInitializer(() => checkAuthOnStart(inject(OidcSecurityService))),
        provideAnimationsAsync(),
        { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
        { provide: MAT_DATE_LOCALE, useValue: 'ru-RU' },
        { provide: HTTP_INTERCEPTORS, useClass: BasicAuthInterceptor, multi: true },
        {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,useValue: {
                subscriptSizing: 'dynamic'
            }},
        {provide: PERSON_IAS_SPORTSMAN, useValue: "https://iasnew.sport.mos.ru/persons/"},
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: {
                duration: 2000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['snack-bar']
            }
        },
        {provide: RouteReuseStrategy, useClass: CustomReuseStrategy},
    ]
};
